import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../api/api';
import Swal from 'sweetalert2';

const ManageDashboardLayer = () => {
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/summery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const dashboardCards = [
    {
      key: 'active_members',
      title: 'Total Members',
      value: dashboard.active_members ?? 0,
      icon: '👥',
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      iconBg: '#667eea'
    },
    {
      key: 'active_cash_loans',
      title: 'Total Cash Loans',
      value: dashboard.active_cash_loans ?? 0,
      icon: '💰',
      color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      iconBg: '#f5576c'
    },
    {
      key: 'active_product_loans',
      title: 'Total Product Loans',
      value: dashboard.active_product_loans ?? 0,
      icon: '📦',
      color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      iconBg: '#4facfe'
    },
    {
      key: 'active_groups',
      title: 'Total Groups',
      value: dashboard.active_groups ?? 0,
      icon: '👨‍👩‍👧‍👦',
      color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      iconBg: '#43e97b'
    },
    {
      key: 'total_cash_received',
      title: 'Total Cash Received',
      value: dashboard.total_cash_received ?? 0,
      icon: '💵',
      color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      iconBg: '#fa709a'
    },
    {
      key: 'total_product_received',
      title: 'Product EMI Collected',
      value: dashboard.total_product_received ?? 0,
      icon: '📊',
      color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      iconBg: '#a8edea'
    },
    {
      key: 'today_product_received',
      title: "Today's Product Collection",
      value: dashboard.today_product_received ?? 0,
      icon: '📅',
      color: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
      iconBg: '#d299c2'
    },
    {
      key: 'today_cash_received',
      title: "Today's Cash Collection",
      value: dashboard.today_cash_received ?? 0,
      icon: '💳',
      color: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
      iconBg: '#66a6ff'
    }
  ];

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
        <style jsx>{`
          .dashboard-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
          }
          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard Overview</h1>
        <button onClick={fetchDashboardData} className="refresh-btn">
          ↻ Refresh
        </button>
      </div>

      <div className="dashboard-grid">
        {dashboardCards.map((card) => (
          <div key={card.key} className="dashboard-card">
            <div
              className="card-gradient"
              style={{ background: card.color }}
            >
              <div className="card-icon" style={{ backgroundColor: card.iconBg }}>
                <span className="icon-emoji">{card.icon}</span>
              </div>
              <div className="card-content">
                <h3 className="card-title">{card.title}</h3>
                <div className="card-value">{formatNumber(card.value)}</div>
              </div>
              <div className="card-wave">
                <svg viewBox="0 0 100 20" preserveAspectRatio="none">
                  <path d="M0,10 C20,5 40,15 60,10 C80,5 100,15 100,10 L100,20 L0,20 Z" fill="rgba(255,255,255,0.1)" />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .dashboard-container {
          padding: 24px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #eaeaea;
        }

        .dashboard-title {
          font-size: 28px !important;
          font-weight: 600 !important;
          color: #2d3748 !important;
          margin: 0 !important;
        }

        .refresh-btn {
          background: #667eea !important;
          color: white !important;
          border: none !important;
          padding: 10px 20px !important;
          border-radius: 8px !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
        }

        .refresh-btn:hover {
          background: #5a67d8;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
        }

        .dashboard-card {
          height: 140px;
          perspective: 1000px;
        }

        .card-gradient {
          height: 100% !important;
          border-radius: 16px !important;
          padding: 24px !important;
          position: relative !important;
          overflow: hidden !important;
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
          transition: all 0.4s ease;
          display: flex;
          align-items: center;
          gap: 20px;
          color: white;
        }

        .dashboard-card:hover .card-gradient {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
        }

        .card-icon {
          width: 60px !important;
          height: 60px !important;
          border-radius: 12px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 28px !important;
          flex-shrink: 0 !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
        }

        .card-content {
          flex: 1;
          z-index: 2;
        }

        .card-title {
          font-size: 14px !important;
          font-weight: 500 !important;
          margin: 0 0 8px 0 !important;
          opacity: 0.9 !important;
          letter-spacing: 0.5px !important;
        }

        .card-value {
          font-size: 28px !important;
          font-weight: 700 !important;
          margin: 0 !important;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .card-wave {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 30px;
          z-index: 1;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .dashboard-grid {
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .dashboard-container {
            padding: 16px;
          }
          
          .dashboard-grid {
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
            gap: 16px;
          }
          
          .dashboard-title {
            font-size: 24px;
          }
          
          .card-gradient {
            padding: 20px;
          }
          
          .card-value {
            font-size: 24px;
          }
          
          .card-icon {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }
        }

        @media (max-width: 480px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
          
          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
          }
          
          .refresh-btn {
            align-self: stretch;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default ManageDashboardLayer;